/* Oreganix sections: media slider, tabs, reading progress, back-to-top, promo popup, order tracking,
   before/after, results, hotspots, video hero, parallax, timeline, sticky CTA, Lottie loader.
   No external requests. */

(() => {
  /* ---------- Media slider (multi-slide, scroll-snap) ---------- */
  if (!customElements.get('ox-media-slider')) {
    customElements.define(
      'ox-media-slider',
      class OxMediaSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.slides = [...this.querySelectorAll('[data-slide]')];
          this.dots = this.querySelector('[data-dots]');
          this.loop = this.dataset.type !== 'slide';
          this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => this.step(-1)));
          this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => this.step(1)));
          if (this.dots) {
            this.dots.innerHTML = this.slides
              .map((_, i) => `<button type="button" class="ox-slider__dot" data-dot="${i}" aria-label="${i + 1}"></button>`)
              .join('');
            this.dots.addEventListener('click', (e) => {
              const dot = e.target.closest('[data-dot]');
              if (dot) this.goTo(Number(dot.dataset.dot));
            });
          }
          this.track.addEventListener('scroll', () => this.sync(), { passive: true });
          if (this.dataset.autoplay === 'true') {
            const speed = (Number(this.dataset.speed) || 5) * 1000;
            this.timer = setInterval(() => this.step(1), speed);
            this.addEventListener('pointerenter', () => clearInterval(this.timer));
          }
          this.sync();
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        get perMove() {
          return Number(window.matchMedia('(min-width: 750px)').matches ? this.dataset.moveDesktop : this.dataset.moveMobile) || 1;
        }

        current() {
          const left = this.track.scrollLeft;
          let best = 0;
          this.slides.forEach((s, i) => {
            if (Math.abs(s.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.slides[best].offsetLeft - this.track.offsetLeft - left)) best = i;
          });
          return best;
        }

        goTo(index) {
          const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
          if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        }

        step(direction) {
          const atEnd = Math.ceil(this.track.scrollLeft + this.track.clientWidth) >= this.track.scrollWidth - 2;
          const atStart = this.track.scrollLeft <= 2;
          if (direction > 0 && atEnd) return this.loop || this.timer ? this.goTo(0) : null;
          if (direction < 0 && atStart) return this.loop ? this.goTo(this.slides.length - 1) : null;
          this.goTo(this.current() + direction * this.perMove);
        }

        sync() {
          const i = this.current();
          this.dots?.querySelectorAll('[data-dot]').forEach((d, n) => d.classList.toggle('is-active', n === i));
        }
      }
    );
  }

  /* ---------- Tabs ---------- */
  if (!customElements.get('ox-tabs')) {
    customElements.define(
      'ox-tabs',
      class OxTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = [...this.querySelectorAll('[role="tab"]')];
          this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
          this.tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => this.select(i));
            tab.addEventListener('keydown', (e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
                this.select(next);
                this.tabs[next].focus();
              }
            });
          });
        }

        select(index) {
          this.tabs.forEach((t, i) => {
            t.setAttribute('aria-selected', String(i === index));
            t.tabIndex = i === index ? 0 : -1;
          });
          this.panels.forEach((p, i) => (p.hidden = i !== index));
        }
      }
    );
  }

  /* ---------- Reading progress ---------- */
  if (!customElements.get('ox-scroll-progress')) {
    customElements.define(
      'ox-scroll-progress',
      class OxScrollProgress extends HTMLElement {
        connectedCallback() {
          this.bar = this.querySelector('span');
          this.onScroll = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            this.bar.style.width = `${max > 0 ? (window.scrollY / max) * 100 : 0}%`;
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }
      }
    );
  }

  /* ---------- Back to top ---------- */
  if (!customElements.get('ox-back-to-top')) {
    customElements.define(
      'ox-back-to-top',
      class OxBackToTop extends HTMLElement {
        connectedCallback() {
          const offset = Number(this.dataset.offset) || 400;
          this.onScroll = () => this.classList.toggle('is-visible', window.scrollY > offset);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.querySelector('button').addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
          this.onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }
      }
    );
  }

  /* ---------- Promo popup ---------- */
  if (!customElements.get('ox-promo-popup')) {
    customElements.define(
      'ox-promo-popup',
      class OxPromoPopup extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.key = `oxPopupClosed:${this.dataset.id}`;
          this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
          this.dialog.addEventListener('click', (e) => {
            if (e.target === this.dialog) this.close();
          });
          this.dialog.addEventListener('cancel', () => this.remember());

          // Show the success state after Shopify's customer form reloads the page.
          if (this.dataset.posted === 'true') return this.open();
          if (this.dataset.designMode === 'true') return;
          if (this.recentlyClosed()) return;
          setTimeout(() => this.open(), (Number(this.dataset.delay) || 5) * 1000);
        }

        recentlyClosed() {
          try {
            const at = Number(localStorage.getItem(this.key));
            const days = Number(this.dataset.frequency) || 7;
            return at && Date.now() - at < days * 86400000;
          } catch (e) {
            return false;
          }
        }

        remember() {
          try {
            localStorage.setItem(this.key, String(Date.now()));
          } catch (e) {}
        }

        open() {
          if (!this.dialog.open) this.dialog.showModal();
        }

        close() {
          this.remember();
          this.dialog.close();
        }
      }
    );
  }

  /* ---------- Order tracking ---------- */
  if (!customElements.get('ox-track-order')) {
    customElements.define(
      'ox-track-order',
      class OxTrackOrder extends HTMLElement {
        connectedCallback() {
          this.querySelector('form').addEventListener('submit', (e) => {
            e.preventDefault();
            const number = this.querySelector('input').value.trim();
            const error = this.querySelector('[data-error]');
            if (!number) {
              error.hidden = false;
              return;
            }
            error.hidden = true;
            const template = this.dataset.url;
            // Only the customer's own click opens the carrier page; nothing is sent in the background.
            if (template && template.includes('{number}')) {
              window.open(template.replace('{number}', encodeURIComponent(number)), '_blank', 'noopener');
            } else {
              window.location.href = this.dataset.fallback;
            }
          });
        }
      }
    );
  }

  /* ---------- Video reels ---------- */
  if (!customElements.get('ox-video-reels')) {
    customElements.define(
      'ox-video-reels',
      class OxVideoReels extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.dialog = this.querySelector('[data-dialog]');
          this.stage = this.querySelector('[data-stage]');
          this.sound = this.querySelector('[data-sound]');
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.scroll(-1));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.scroll(1));
          this.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => this.open(b)));
          this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
          this.dialog.addEventListener('close', () => (this.stage.innerHTML = ''));
          this.dialog.addEventListener('click', (e) => {
            if (e.target === this.dialog) this.dialog.close();
          });
          this.sound.addEventListener('click', () => {
            const v = this.stage.querySelector('video');
            if (!v) return;
            v.muted = !v.muted;
            this.sound.textContent = v.muted ? '🔇' : '🔊';
          });
          if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) =>
                entries.forEach((entry) => {
                  if (entry.isIntersecting) entry.target.play().catch(() => {});
                  else entry.target.pause();
                }),
              { threshold: 0.6 }
            );
            this.querySelectorAll('.ox-reels__video').forEach((v) => this.observer.observe(v));
          }
        }

        disconnectedCallback() {
          this.observer?.disconnect();
        }

        scroll(dir) {
          const card = this.track.querySelector('[data-reel]');
          const step = card ? card.getBoundingClientRect().width + 16 : 240;
          this.track.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
        }

        open(button) {
          const source = button.querySelector('video');
          if (!source) return;
          const video = source.cloneNode(true);
          video.removeAttribute('class');
          video.controls = true;
          video.muted = false;
          this.stage.innerHTML = '';
          this.stage.append(video);
          const product = button.parentElement.querySelector('.ox-reels__product');
          if (product) this.stage.append(product.cloneNode(true));
          this.sound.textContent = '🔊';
          this.dialog.showModal();
          video.play().catch(() => {
            video.muted = true;
            this.sound.textContent = '🔇';
            video.play().catch(() => {});
          });
        }
      }
    );
  }

  /* ---------- Stories ---------- */
  if (!customElements.get('ox-stories')) {
    customElements.define(
      'ox-stories',
      class OxStories extends HTMLElement {
        connectedCallback() {
          this.viewer = this.querySelector('[data-viewer]');
          this.slides = [...this.querySelectorAll('[data-slide]')];
          this.bars = [...this.querySelectorAll('[data-bars] i')];
          this.duration = (Number(this.dataset.duration) || 5) * 1000;
          this.querySelectorAll('[data-story]').forEach((b) => b.addEventListener('click', () => this.open(Number(b.dataset.story))));
          this.querySelector('[data-close]').addEventListener('click', () => this.viewer.close());
          this.querySelector('[data-prev]').addEventListener('click', () => this.show(this.index - 1));
          this.querySelector('[data-next]').addEventListener('click', () => this.show(this.index + 1));
          this.viewer.addEventListener('close', () => this.stop());
          this.viewer.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') this.show(this.index + 1);
            if (e.key === 'ArrowLeft') this.show(this.index - 1);
          });
        }

        open(index) {
          this.viewer.showModal();
          this.show(index);
        }

        stop() {
          cancelAnimationFrame(this.frame);
          this.slides.forEach((s) => s.querySelector('video')?.pause());
        }

        show(index) {
          if (index < 0) index = 0;
          if (index >= this.slides.length) return this.viewer.close();
          this.stop();
          this.index = index;
          this.slides.forEach((s, i) => (s.hidden = i !== index));
          this.bars.forEach((b, i) => (b.style.width = i < index ? '100%' : '0%'));
          const video = this.slides[index].querySelector('video');
          let length = this.duration;
          if (video) {
            video.currentTime = 0;
            video.play().catch(() => {});
            if (video.duration) length = video.duration * 1000;
            else video.addEventListener('loadedmetadata', () => (length = video.duration * 1000), { once: true });
          }
          const start = performance.now();
          const step = (now) => {
            const progress = Math.min(1, (now - start) / length);
            if (this.bars[index]) this.bars[index].style.width = `${progress * 100}%`;
            if (progress >= 1) return this.show(index + 1);
            this.frame = requestAnimationFrame(step);
          };
          this.frame = requestAnimationFrame(step);
        }
      }
    );
  }

  /* ---------- Music player ---------- */
  if (!customElements.get('ox-music-player')) {
    customElements.define(
      'ox-music-player',
      class OxMusicPlayer extends HTMLElement {
        connectedCallback() {
          this.audio = this.querySelector('audio');
          this.button = this.querySelector('button');
          this.audio.volume = Number(this.dataset.volume) || 0.5;
          this.button.addEventListener('click', () => (this.audio.paused ? this.play(true) : this.pause()));
          this.audio.addEventListener('timeupdate', () => this.save());
          // Sound needs a user gesture: if the visitor had music on, resume on their first interaction.
          if (this.dataset.remember === 'true' && this.read().playing) {
            document.addEventListener('pointerdown', () => this.play(false), { once: true });
          }
        }

        read() {
          try {
            return JSON.parse(sessionStorage.getItem('oxMusic') || '{}');
          } catch (e) {
            return {};
          }
        }

        save() {
          if (this.dataset.remember !== 'true') return;
          try {
            sessionStorage.setItem('oxMusic', JSON.stringify({ playing: !this.audio.paused, time: this.audio.currentTime }));
          } catch (e) {}
        }

        play(fromStart) {
          const state = this.read();
          if (!fromStart && state.time) this.audio.currentTime = state.time;
          this.audio
            .play()
            .then(() => {
              this.classList.add('is-playing');
              this.button.setAttribute('aria-pressed', 'true');
              this.save();
            })
            .catch(() => {});
        }

        pause() {
          this.audio.pause();
          this.classList.remove('is-playing');
          this.button.setAttribute('aria-pressed', 'false');
          this.save();
        }
      }
    );
  }

  /* ---------- Article table of contents ---------- */
  if (!customElements.get('ox-toc')) {
    customElements.define(
      'ox-toc',
      class OxToc extends HTMLElement {
        connectedCallback() {
          const content = document.querySelector('.article-template__content');
          const list = this.querySelector('[data-list]');
          const toggle = this.querySelector('.ox-toc__toggle');
          const headings = content ? [...content.querySelectorAll(this.dataset.levels || 'h2,h3')] : [];
          if (!headings.length) {
            this.hidden = true;
            return;
          }
          headings.forEach((h) => {
            if (!h.id) {
              const base =
                h.textContent
                  .trim()
                  .toLowerCase()
                  .replace(/[^\p{L}\p{N}]+/gu, '-')
                  .replace(/^-|-$/g, '') || 'section';
              let id = base;
              let n = 2;
              while (document.getElementById(id)) id = `${base}-${n++}`;
              h.id = id;
            }
            const li = document.createElement('li');
            li.className = `ox-toc__item ox-toc__item--${h.tagName.toLowerCase()}`;
            const a = document.createElement('a');
            a.href = `#${h.id}`;
            a.textContent = h.textContent.trim();
            li.append(a);
            list.append(li);
          });
          toggle.addEventListener('click', () => {
            const collapsed = this.classList.toggle('is-collapsed');
            toggle.setAttribute('aria-expanded', String(!collapsed));
          });
          if ('IntersectionObserver' in window) {
            const links = [...list.querySelectorAll('a')];
            const observer = new IntersectionObserver(
              (entries) =>
                entries.forEach((e) => {
                  if (e.isIntersecting) links.forEach((l) => l.classList.toggle('is-active', l.hash === `#${e.target.id}`));
                }),
              { rootMargin: '0px 0px -70% 0px' }
            );
            headings.forEach((h) => observer.observe(h));
          }
        }
      }
    );
  }

  /* ---------- Article product promotion ---------- */
  if (!customElements.get('ox-article-promo')) {
    customElements.define(
      'ox-article-promo',
      class OxArticlePromo extends HTMLElement {
        connectedCallback() {
          if (this.dataset.display !== 'sticky') return;
          this.querySelector('[data-close]')?.addEventListener('click', () => {
            this.classList.add('is-dismissed');
            this.classList.remove('is-visible');
          });
          this.onScroll = () => {
            if (this.classList.contains('is-dismissed')) return;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            this.classList.toggle('is-visible', max > 0 && window.scrollY / max > 0.2);
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        }
      }
    );
  }

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Calls `callback` once when `element` first scrolls into view.
  const whenVisible = (element, callback, rootMargin = '0px 0px -10% 0px') => {
    if (!('IntersectionObserver' in window)) return callback();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin }
    );
    observer.observe(element);
    return observer;
  };

  // Adds one variant through window.oxProduct (ox-product.js) and shows feedback on the button.
  const addVariantFromButton = async (button, variantId, addedLabel, errorEl) => {
    const label = button.querySelector('[data-label]');
    const original = label?.textContent;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    if (errorEl) errorEl.hidden = true;
    try {
      if (!window.oxProduct) throw new Error('Cart is not ready yet, please try again.');
      await window.oxProduct.addToCart([{ id: Number(variantId), quantity: 1 }]);
      if (label && addedLabel) {
        label.textContent = addedLabel;
        setTimeout(() => (label.textContent = original), 2500);
      }
      return true;
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
      return false;
    } finally {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  };

  /* ---------- Before & after slider ---------- */
  if (!customElements.get('ox-before-after')) {
    customElements.define(
      'ox-before-after',
      class OxBeforeAfter extends HTMLElement {
        connectedCallback() {
          this.vertical = this.dataset.orientation === 'vertical';
          this.slider = this.querySelector('[role="slider"]');
          this.position = parseFloat(this.slider?.getAttribute('aria-valuenow')) || 50;
          this.addEventListener('pointerdown', (event) => {
            if (event.button !== 0) return;
            this.dragging = true;
            this.setPointerCapture(event.pointerId);
            this.classList.add('is-dragging');
            this.moveTo(event);
          });
          this.addEventListener('pointermove', (event) => this.dragging && this.moveTo(event));
          const stop = () => {
            this.dragging = false;
            this.classList.remove('is-dragging');
          };
          this.addEventListener('pointerup', stop);
          this.addEventListener('pointercancel', stop);
          this.slider?.addEventListener('keydown', (event) => {
            const step = event.shiftKey ? 10 : 2;
            const keys = { ArrowLeft: -step, ArrowUp: -step, ArrowRight: step, ArrowDown: step, Home: -100, End: 100 };
            if (!(event.key in keys)) return;
            event.preventDefault();
            this.set(this.position + keys[event.key]);
          });
        }

        moveTo(event) {
          const rect = this.getBoundingClientRect();
          const ratio = this.vertical ? (event.clientY - rect.top) / rect.height : (event.clientX - rect.left) / rect.width;
          this.set(ratio * 100);
        }

        set(value) {
          this.position = Math.min(100, Math.max(0, value));
          this.style.setProperty('--ox-ba-pos', `${this.position}%`);
          this.slider?.setAttribute('aria-valuenow', Math.round(this.position));
        }
      }
    );
  }

  /* ---------- Count-up numbers (results) ---------- */
  if (!customElements.get('ox-count-up')) {
    customElements.define(
      'ox-count-up',
      class OxCountUp extends HTMLElement {
        connectedCallback() {
          const counters = [...this.querySelectorAll('[data-count]')];
          const duration = (parseFloat(this.dataset.duration) || 0) * 1000;
          if (!duration || reducedMotion()) {
            this.classList.add('is-visible');
            return;
          }
          this.classList.add('is-ready');
          counters.forEach((c) => (c.textContent = '0'));
          whenVisible(this, () => {
            this.classList.add('is-visible');
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              counters.forEach((c) => {
                const target = parseFloat(c.dataset.count) || 0;
                const decimals = (String(c.dataset.count).split('.')[1] || '').length;
                c.textContent = (target * eased).toFixed(decimals);
              });
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          });
        }
      }
    );
  }

  /* ---------- Social comments: like toggle (visual only) ---------- */
  if (!customElements.get('ox-social-thread')) {
    customElements.define(
      'ox-social-thread',
      class OxSocialThread extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-like]');
            if (!button) return;
            const liked = button.getAttribute('aria-pressed') !== 'true';
            button.setAttribute('aria-pressed', String(liked));
            const count = button.closest('.ox-comment')?.querySelector('[data-like-count]');
            if (count) count.textContent = String((parseInt(count.textContent, 10) || 0) + (liked ? 1 : -1));
          });
        }
      }
    );
  }

  /* ---------- Shoppable image hotspots ---------- */
  if (!customElements.get('ox-hotspots')) {
    customElements.define(
      'ox-hotspots',
      class OxHotspots extends HTMLElement {
        connectedCallback() {
          this.spots = [...this.querySelectorAll('[data-hotspot]')];
          this.addEventListener('click', (event) => {
            const dot = event.target.closest('.ox-hotspot__dot');
            if (dot) {
              const spot = dot.closest('[data-hotspot]');
              this.toggle(spot, !spot.classList.contains('is-open'));
              return;
            }
            if (event.target.closest('[data-close]')) {
              const spot = event.target.closest('[data-hotspot]');
              this.toggle(spot, false);
              spot.querySelector('.ox-hotspot__dot')?.focus();
              return;
            }
            const add = event.target.closest('[data-add]');
            if (add) {
              const spot = add.closest('[data-hotspot]');
              addVariantFromButton(add, add.dataset.variantId, this.dataset.added, spot.querySelector('[data-error]'));
            }
          });
          this.addEventListener('change', (event) => {
            if (!event.target.matches('[data-variant]')) return;
            const spot = event.target.closest('[data-hotspot]');
            const option = event.target.selectedOptions[0];
            const add = spot.querySelector('[data-add]');
            if (add) add.dataset.variantId = option.value;
            const price = spot.querySelector('[data-price]');
            if (price && window.oxProduct) price.textContent = window.oxProduct.formatMoney(option.dataset.price, this.dataset.moneyFormat);
          });
          this.onDocClick = (event) => {
            if (!this.contains(event.target)) this.spots.forEach((s) => this.toggle(s, false));
          };
          this.onKey = (event) => {
            if (event.key === 'Escape') this.spots.forEach((s) => this.toggle(s, false));
          };
          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onKey);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKey);
        }

        toggle(spot, open) {
          if (!spot) return;
          if (open) this.spots.forEach((s) => s !== spot && this.toggle(s, false));
          spot.classList.toggle('is-open', open);
          spot.querySelector('.ox-hotspot__dot')?.setAttribute('aria-expanded', String(open));
          const card = spot.querySelector('.ox-hotspot__card');
          if (card) card.hidden = !open;
        }
      }
    );
  }

  /* ---------- Product features: highlight marker for hovered feature ---------- */
  if (!customElements.get('ox-product-features')) {
    customElements.define(
      'ox-product-features',
      class OxProductFeatures extends HTMLElement {
        connectedCallback() {
          const highlight = (index) =>
            this.querySelectorAll('[data-marker]').forEach((m) => m.classList.toggle('is-active', m.dataset.marker === index));
          this.querySelectorAll('[data-feature]').forEach((feature) => {
            feature.addEventListener('pointerenter', () => highlight(feature.dataset.feature));
            feature.addEventListener('pointerleave', () => highlight(null));
          });
        }
      }
    );
  }

  /* ---------- Hero slideshow with background video ---------- */
  if (!customElements.get('ox-hero-slideshow')) {
    customElements.define(
      'ox-hero-slideshow',
      class OxHeroSlideshow extends HTMLElement {
        connectedCallback() {
          this.slides = [...this.querySelectorAll('[data-slide]')];
          this.dots = [...this.querySelectorAll('[data-dot]')];
          this.index = 0;
          this.paused = false;
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
          this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot))));
          this.pauseButton = this.querySelector('[data-pause]');
          this.pauseButton?.addEventListener('click', () => this.setPaused(!this.paused));
          this.addEventListener('focusin', () => this.stop());
          this.addEventListener('focusout', () => this.start());
          // Do not play videos offscreen.
          this.inView = true;
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(([entry]) => {
              this.inView = entry.isIntersecting;
              this.syncVideos();
            });
            this.observer.observe(this);
          }
          if (reducedMotion()) this.setPaused(true);
          this.go(0);
        }

        disconnectedCallback() {
          this.stop();
          this.observer?.disconnect();
        }

        setPaused(paused) {
          this.paused = paused;
          this.pauseButton?.setAttribute('aria-pressed', String(paused));
          this.pauseButton?.classList.toggle('is-paused', paused);
          paused ? this.stop() : this.start();
        }

        start() {
          this.stop();
          if (this.paused || this.dataset.autoplay !== 'true' || this.slides.length < 2) return;
          this.timer = setInterval(() => this.go(this.index + 1), (Number(this.dataset.speed) || 8) * 1000);
        }

        stop() {
          clearInterval(this.timer);
          this.timer = null;
        }

        go(index) {
          const count = this.slides.length;
          if (!count) return;
          this.index = (index + count) % count;
          this.slides.forEach((slide, i) => {
            const active = i === this.index;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', String(!active));
            slide.toggleAttribute('inert', !active);
          });
          this.dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === this.index);
            dot.setAttribute('aria-current', String(i === this.index));
          });
          this.syncVideos();
          // Restart the timer so a manual change gets a full interval.
          if (!this.contains(document.activeElement)) this.start();
        }

        syncVideos() {
          this.slides.forEach((slide, i) => {
            const video = slide.querySelector('video');
            if (!video) return;
            if (i === this.index && this.inView && !reducedMotion()) {
              video.muted = true;
              video.play()?.catch(() => {});
            } else {
              video.pause();
            }
          });
        }
      }
    );
  }

  /* ---------- 3D parallax hero ---------- */
  if (!customElements.get('ox-parallax')) {
    customElements.define(
      'ox-parallax',
      class OxParallax extends HTMLElement {
        connectedCallback() {
          if (reducedMotion()) return;
          this.layers = [...this.querySelectorAll('[data-depth]')].map((el) => ({ el, depth: (Number(el.dataset.depth) || 0) / 100 }));
          this.tiltTarget = this.dataset.tilt === 'true' ? this.querySelector('[data-tilt-target]') : null;
          this.strength = (Number(this.dataset.intensity) || 5) * 6;
          this.pointer = { x: 0, y: 0 };
          this.scroll = 0;
          const mode = this.dataset.mode;
          const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
          if ((mode === 'pointer' || mode === 'both') && finePointer) {
            this.addEventListener('pointermove', (event) => {
              const rect = this.getBoundingClientRect();
              this.pointer.x = (event.clientX - rect.left) / rect.width - 0.5;
              this.pointer.y = (event.clientY - rect.top) / rect.height - 0.5;
              this.queue();
            });
            this.addEventListener('pointerleave', () => {
              this.pointer = { x: 0, y: 0 };
              this.queue();
            });
          }
          if (mode === 'scroll' || mode === 'both') {
            this.onScroll = () => {
              const rect = this.getBoundingClientRect();
              if (rect.bottom < 0 || rect.top > window.innerHeight) return;
              // -0.5 (entering from below) .. 0.5 (leaving at the top)
              this.scroll = (window.innerHeight / 2 - (rect.top + rect.height / 2)) / (window.innerHeight + rect.height);
              this.queue();
            };
            window.addEventListener('scroll', this.onScroll, { passive: true });
            this.onScroll();
          }
          this.classList.add('is-active');
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        }

        queue() {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.render();
          });
        }

        render() {
          const s = this.strength;
          this.layers.forEach(({ el, depth }) => {
            const x = -this.pointer.x * s * depth;
            const y = -this.pointer.y * s * depth + this.scroll * s * 6 * depth;
            el.style.setProperty('--tx', `${x.toFixed(2)}px`);
            el.style.setProperty('--ty', `${y.toFixed(2)}px`);
          });
          if (this.tiltTarget) {
            this.tiltTarget.style.transform = `perspective(900px) rotateX(${(this.pointer.y * -8).toFixed(2)}deg) rotateY(${(this.pointer.x * 10).toFixed(2)}deg)`;
          }
        }
      }
    );
  }

  /* ---------- Reveal items on scroll (timeline) ---------- */
  if (!customElements.get('ox-reveal-list')) {
    customElements.define(
      'ox-reveal-list',
      class OxRevealList extends HTMLElement {
        connectedCallback() {
          const items = [...this.querySelectorAll('[data-reveal]')];
          if (!('IntersectionObserver' in window) || reducedMotion()) {
            items.forEach((i) => i.classList.add('is-revealed'));
            return;
          }
          this.classList.add('is-ready');
          this.observer = new IntersectionObserver(
            (entries) =>
              entries.forEach((e) => {
                if (!e.isIntersecting) return;
                e.target.classList.add('is-revealed');
                this.observer.unobserve(e.target);
              }),
            { rootMargin: '0px 0px -12% 0px' }
          );
          items.forEach((i) => this.observer.observe(i));
        }

        disconnectedCallback() {
          this.observer?.disconnect();
        }
      }
    );
  }

  /* ---------- Sticky call-to-action bar ---------- */
  if (!customElements.get('ox-sticky-cta')) {
    customElements.define(
      'ox-sticky-cta',
      class OxStickyCta extends HTMLElement {
        connectedCallback() {
          this.key = `ox-sticky-cta-${this.dataset.id}`;
          this.designMode = !!window.Shopify?.designMode;
          this.side = this.classList.contains('ox-sticky-cta--top') ? 'top' : 'bottom';
          try {
            if (!this.designMode && sessionStorage.getItem(this.key)) return;
          } catch (e) {}
          this.offset = Number(this.dataset.offset) || 0;
          this.footerVisible = false;
          const footer = document.querySelector('.shopify-section-group-footer-group, footer');
          if (this.dataset.hideNearFooter === 'true' && footer && 'IntersectionObserver' in window) {
            this.footerObserver = new IntersectionObserver(([entry]) => {
              this.footerVisible = entry.isIntersecting;
              this.update();
            });
            this.footerObserver.observe(footer);
          }
          this.onScroll = () => this.update();
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.querySelector('[data-close]')?.addEventListener('click', () => {
            this.dismissed = true;
            try {
              sessionStorage.setItem(this.key, '1');
            } catch (e) {}
            this.update();
          });
          const add = this.querySelector('[data-add]');
          add?.addEventListener('click', () => addVariantFromButton(add, add.dataset.variantId, this.dataset.added));
          this.update();
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          this.footerObserver?.disconnect();
          document.body.classList.remove(`has-ox-sticky-cta-${this.side}`);
        }

        update() {
          const visible = this.designMode || (!this.dismissed && window.scrollY >= this.offset && !this.footerVisible);
          this.classList.toggle('is-visible', visible);
          document.body.classList.toggle(`has-ox-sticky-cta-${this.side}`, visible);
        }
      }
    );
  }

  /* ---------- Lottie JSON animation (self-hosted player) ---------- */
  let lottieLoader;
  const loadLottie = (src) => {
    if (window.lottie) return Promise.resolve(window.lottie);
    lottieLoader ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.append(script);
    });
    return lottieLoader;
  };

  if (!customElements.get('ox-lottie')) {
    customElements.define(
      'ox-lottie',
      class OxLottie extends HTMLElement {
        connectedCallback() {
          this.visibility = whenVisible(this, () => this.init(), '200px 0px');
        }

        disconnectedCallback() {
          this.visibility?.disconnect?.();
          this.viewObserver?.disconnect();
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          this.animation?.destroy();
        }

        async init() {
          const play = this.dataset.play;
          try {
            const lottie = await loadLottie(this.dataset.player);
            this.animation = lottie.loadAnimation({
              container: this,
              renderer: 'svg',
              loop: this.dataset.loop === 'true' && play !== 'scroll',
              autoplay: false,
              path: this.dataset.src,
              rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
            });
          } catch (error) {
            console.error('Lottie failed to load', error);
            return;
          }
          const anim = this.animation;
          anim.setSpeed(Number(this.dataset.speed) || 1);
          anim.addEventListener('data_failed', () => this.classList.add('is-error'));
          anim.addEventListener('DOMLoaded', () => {
            this.classList.add('is-loaded');
            if (reducedMotion()) return anim.goToAndStop(anim.totalFrames - 1, true);
            if (play === 'hover') {
              this.addEventListener('pointerenter', () => anim.play());
              this.addEventListener('pointerleave', () => anim.pause());
            } else if (play === 'scroll') {
              this.onScroll = () => {
                const rect = this.getBoundingClientRect();
                const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
                anim.goToAndStop(progress * (anim.totalFrames - 1), true);
              };
              window.addEventListener('scroll', this.onScroll, { passive: true });
              this.onScroll();
            } else {
              this.viewObserver = new IntersectionObserver(([entry]) => (entry.isIntersecting ? anim.play() : anim.pause()));
              this.viewObserver.observe(this);
            }
          });
        }
      }
    );
  }
})();
